/**
 *  @class
 *  @function FacetsToggle
 */
class FacetsToggle {
  constructor() {
    this.container = document.getElementById("Facet-Drawer");
    const button = document.getElementById("Facets-Toggle");
    this.mobileBreakpoint = 768; // Adjust this to your mobile breakpoint

    // Add functionality to buttons
    if (button) {
      button.addEventListener("click", (e) => {
        e.preventDefault();
        document.body.classList.toggle("open-cc");
        this.container.classList.toggle("active");
        this.container.removeAttribute("inert");
      });
    }

    // Auto-close on resize (handles both orientation change and screen width)
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this.handleResize);

    // Also listen for orientation change specifically (better mobile support)
    window.addEventListener("orientationchange", () => {
      // Small delay to let the browser finish rotating
      setTimeout(() => this.closeDrawer(), 100);
    });
  }

  handleResize() {
    const isLandscape = window.innerWidth > window.innerHeight;
    const isDesktop = window.innerWidth >= this.mobileBreakpoint;

    if (isLandscape || isDesktop) {
      this.closeDrawer();
    }
  }

  closeDrawer() {
    if (this.container?.classList.contains("active")) {
      document.body.classList.remove("open-cc");
      this.container.classList.remove("active");
      this.container.setAttribute("inert", "");
    }
  }
}

/**
 *  @class
 *  @function SidebarToggle
 */
class SidebarToggle {
  constructor() {
    this.container = document.getElementById("Sidebar-Container");
    this.productGrid = document.getElementById("product-grid");
    const button = document.getElementById("Sidebar-Toggle");

    // Remember the viewport WIDTH at start. Mobile browsers fire a `resize`
    // when the address bar hides/shows during scroll, but that only changes
    // HEIGHT, not width. Infinite scroll needs scrolling, so those spurious
    // resizes were force-closing the sidebar. We now ignore height-only ones.
    this.lastWidth = window.innerWidth;

    if (button) {
      const span = button.querySelector("span");

      button.addEventListener("click", (e) => {
        e.preventDefault();
        this.container.classList.toggle("facets--sidebar-open");

        span.textContent = this.container.classList.contains("facets--sidebar-open")
          ? theme.strings.hideFilters
          : theme.strings.showFilters;

        this.toggleGridColumns();
      });
    }

    // Only react to a REAL width/orientation change, not height-only resizes.
    window.addEventListener("resize", () => {
      const currentWidth = window.innerWidth;
      if (currentWidth === this.lastWidth) return; // height-only (scroll) → ignore
      this.lastWidth = currentWidth;

      this.collapseFiltersIfNeeded();
      this.toggleGridColumns();
    });

    // Run once on load
    this.collapseFiltersIfNeeded();
    this.toggleGridColumns();
  }

  shouldCollapseFilters() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isLandscape = w > h;
    const isMobileWidth = w < 768;
    const isPhoneOrTabletLandscape = isLandscape && w < 1200;
    return isMobileWidth || isPhoneOrTabletLandscape;
  }

  collapseFiltersIfNeeded() {
    if (!this.container) return;

    if (this.shouldCollapseFilters()) {
      this.container.classList.remove("facets--sidebar-open");

      const button = document.getElementById("Sidebar-Toggle");
      const span = button ? button.querySelector("span") : null;
      if (span && typeof theme !== "undefined" && theme.strings) {
        span.textContent = theme.strings.showFilters;
      }
    }
  }

  toggleGridColumns() {
    if (!this.productGrid) return;

    const isSidebarOpen = this.container.classList.contains("facets--sidebar-open");
    const screenWidth = window.innerWidth;

    const classes = this.productGrid.className.split(" ");
    const filteredClasses = classes.filter((cls) => !cls.startsWith("medium-up-"));

    let columnsClass;

    if (screenWidth >= 768 && screenWidth <= 1366) {
      // columnsClass = isSidebarOpen ? "medium-up-3" : "medium-up-5";
       columnsClass = "medium-up-4";
    } else if (isSidebarOpen) {
      columnsClass = "medium-up-4";
    } else {
      columnsClass = "medium-up-5";
    }

    filteredClasses.push(columnsClass);
    this.productGrid.className = filteredClasses.join(" ");
  }
}

/**
 *  @class
 *  @function FacetFiltersForm
 */
class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    // "Show more" clicks are DELEGATED so the buttons keep working after a
    // filter render replaces the row HTML (direct bindings die with the
    // replaced nodes).
    this.addEventListener("click", (event) => {
      const moreButton = event.target.closest(".thb-filter-content--button");
      if (moreButton) this.onMore.call(moreButton, event);
    });

    // ACCORDION FIX (shield): something in the theme toggles the <details>
    // when a click inside the row bubbles up (not just clicks on the
    // summary/title). Stop clicks on the filter options themselves from
    // ever bubbling past this element. Capture phase, so it runs before
    // any bubble-phase toggle handlers on collapsible-row / details.
    // Checkbox toggling and the resulting `input` event are default
    // actions/separate events, so they are unaffected.
    this.addEventListener(
      "click",
      (event) => {
        const option = event.target.closest(
          ".thb-filter-content input, .thb-filter-content label"
        );
        if (option) event.stopPropagation();
      },
      true
    );

    // Add input listener but ignore price fields
    this.querySelector("form").addEventListener("input", (event) => {
      // Ignore price input changes - they'll submit on Enter only
      if (event.target.matches(".field__input_min, .field__input_max")) {
        return;
      }
      // ACCORDION FIX (snapshot): capture the open/closed state of every
      // row NOW, at click time, while the row the user is interacting with
      // is still open — not 500ms later when something may have closed it.
      FacetFiltersForm.captureUiState();
      this.debouncedOnSubmit(event);
    });

    // Add Enter key handler for price inputs
    this.querySelector("form").addEventListener("keydown", (event) => {
      if (
        event.target.matches(".field__input_min, .field__input_max") &&
        event.key === "Enter"
      ) {
        event.preventDefault();
        event.target.blur(); // Remove focus
        this.onSubmitHandler(event);
      }
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  /**
   * All forms on the page that contain filter rows (desktop sidebar AND the
   * mobile/drawer copy). NEVER select these by id: the desktop and drawer
   * forms can share the same id, and `querySelectorAll('#id ...')` matches
   * EVERY element with that id — which made the drawer's closed rows
   * overwrite the desktop rows' open state. Pairing by position instead
   * keeps every form's state independent.
   */
  static facetForms(root = document) {
    return Array.from(root.querySelectorAll("form")).filter((form) =>
      form.querySelector("collapsible-row")
    );
  }

  /**
   * Stable identity for a filter row: its title text. Never use
   * data-index for matching — Shopify drops filters with no matching
   * values from the response and renumbers the rest, so positional
   * indexes shift between renders.
   */
  static rowTitle(row) {
    const titleEl =
      row.querySelector("summary") || row.querySelector(".thb-filter-title");
    return titleEl ? titleEl.textContent.trim() : "";
  }

  /**
   * A set of rows can only be matched by title if every row HAS a title
   * and no two rows share one. (The mobile drawer's markup differs from
   * the desktop sidebar — if its rows don't expose titles, title-matching
   * would collapse them all onto one key and rows would vanish.)
   */
  static rowTitlesUsable(rows) {
    const titles = rows.map((row) => FacetFiltersForm.rowTitle(row));
    return (
      titles.every((title) => title !== "") &&
      new Set(titles).size === titles.length
    );
  }

  /**
   * Swap a row's content for the server's version while preserving the
   * open state of its accordion and its "Show more" expansion.
   */
  static updateRowPreservingState(liveRow, parsedRow) {
    const oldDetails = liveRow.querySelector("details");
    const wasOpen = oldDetails ? oldDetails.open : null;
    const wasExpanded = !!liveRow.querySelector("ul[filters-open]");

    liveRow.innerHTML = parsedRow.innerHTML;

    if (wasOpen !== null) {
      const newDetails = liveRow.querySelector("details");
      if (newDetails) {
        if (wasOpen) {
          newDetails.setAttribute("open", "");
        } else {
          newDetails.removeAttribute("open");
        }
      }
    }

    if (wasExpanded) {
      const newList = liveRow.querySelector(".thb-filter-content ul");
      if (newList) newList.setAttribute("filters-open", "");
      const moreButton = liveRow.querySelector(".thb-filter-content--button");
      if (moreButton && typeof theme !== "undefined" && theme.strings) {
        moreButton.textContent = theme.strings.showLess;
      }
    }
  }

  static captureUiState() {
    // One state object per form, in document order, keyed by row title.
    const openRows = FacetFiltersForm.facetForms().map((form) => {
      const rows = {};
      form.querySelectorAll("collapsible-row").forEach((row) => {
        const details = row.querySelector("details");
        if (details) rows[FacetFiltersForm.rowTitle(row)] = details.open;
      });
      return rows;
    });

    const sidebar = document.getElementById("Sidebar-Container");

    FacetFiltersForm.uiState = {
      openRows,
      sidebarOpen: sidebar
        ? sidebar.classList.contains("facets--sidebar-open")
        : null,
    };
  }

  /**
   * Keep the page's scroll position rock-steady while products reload.
   * The fresh grid arrives full of not-yet-loaded lazy images, so the
   * document briefly gets shorter and the browser clamps the scroll
   * position toward the top. Locking the products area's current height
   * during the swap prevents that; the lock is released once the new
   * images have loaded (with a safety-net timeout).
   */
  static productsArea() {
    return (
      document.querySelector(
        "#ProductGridContainer .sidebar-container--products"
      ) || document.getElementById("ProductGridContainer")
    );
  }

  static lockProductsHeight() {
    const products = FacetFiltersForm.productsArea();
    if (!products) return;
    products.style.minHeight = products.offsetHeight + "px";
  }

  static unlockProductsHeightWhenReady() {
    const products = FacetFiltersForm.productsArea();
    if (!products) return;

    const unlock = () => {
      products.style.minHeight = "";
    };

    const pending = Array.from(products.querySelectorAll("img")).filter(
      (img) => !img.complete
    );

    if (!pending.length) {
      requestAnimationFrame(unlock);
      return;
    }

    let remaining = pending.length;
    const done = () => {
      if (--remaining <= 0) unlock();
    };
    pending.forEach((img) => {
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });

    // Safety net: never hold the lock longer than 4s.
    setTimeout(unlock, 4000);
  }

  /**
   * Pair live filter forms with the forms in the AJAX response. Match by
   * form id first — the mobile drawer can be relocated in the DOM at
   * runtime, so document ORDER is not a reliable key — and fall back to
   * position only for forms without an id match.
   */
  static pairForms(liveForms, parsedForms) {
    const pairs = [];
    const usedLive = new Set();

    parsedForms.forEach((parsedForm) => {
      let match = null;
      if (parsedForm.id) {
        match =
          liveForms.find(
            (liveForm) => liveForm.id === parsedForm.id && !usedLive.has(liveForm)
          ) || null;
      }
      if (match) usedLive.add(match);
      pairs.push({ parsedForm, liveForm: match });
    });

    const remaining = liveForms.filter((liveForm) => !usedLive.has(liveForm));
    let nextRemaining = 0;
    pairs.forEach((pair) => {
      if (!pair.liveForm && nextRemaining < remaining.length) {
        pair.liveForm = remaining[nextRemaining++];
      }
    });

    return pairs;
  }

  static applyOpenRows(openRows) {
    FacetFiltersForm.facetForms().forEach((form, formIndex) => {
      const rows = openRows[formIndex];
      if (!rows) return;
      form.querySelectorAll("collapsible-row").forEach((row) => {
        const details = row.querySelector("details");
        if (!details) return;
        const wanted = rows[FacetFiltersForm.rowTitle(row)];
        if (typeof wanted !== "boolean") return;
        if (wanted) {
          details.setAttribute("open", "");
        } else {
          details.removeAttribute("open");
        }
      });
    });
  }

  static restoreUiState() {
    const state = FacetFiltersForm.uiState;
    if (!state) return;

    // Apply now, again on the next frame, and once more after any theme
    // close-animation could have run — so nothing can override the user's
    // open dropdowns behind our back.
    FacetFiltersForm.applyOpenRows(state.openRows);
    requestAnimationFrame(() => FacetFiltersForm.applyOpenRows(state.openRows));
    setTimeout(() => FacetFiltersForm.applyOpenRows(state.openRows), 350);

    const sidebar = document.getElementById("Sidebar-Container");
    if (sidebar && state.sidebarOpen !== null) {
      sidebar.classList.toggle("facets--sidebar-open", state.sidebarOpen);
    }

    // Consume the snapshot: the next interaction takes a fresh one.
    FacetFiltersForm.uiState = null;
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    // Use the snapshot taken at click time if we have one (it saw the rows
    // BEFORE anything could close them); otherwise capture now (popstate,
    // active-filter removal, price slider).
    if (!FacetFiltersForm.uiState) {
      FacetFiltersForm.captureUiState();
    }
    FacetFiltersForm.lockProductsHeight();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const container = document.querySelectorAll(
      ".thb-filter-count, .mobile-filter-count"
    );
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");

    for (var item of container) {
      item.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      if (FacetFiltersForm.filterData.some(filterDataUrl)) {
        FacetFiltersForm.renderSectionFromCache(filterDataUrl, event);
      } else {
        FacetFiltersForm.renderSectionFromFetch(url, event);
      }
    });
    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    // RESPONSE-RACE GUARD: rapid filter interactions fire overlapping
    // fetches, and a slow OLDER response landing after a newer one used to
    // overwrite the page with stale chips/counts/products — leaving the
    // checkboxes saying one thing and the active-filter chips another.
    // Abort any in-flight request and only ever render the latest one.
    const requestId = ++FacetFiltersForm.latestRequestId;
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }
    FacetFiltersForm.abortController =
      typeof AbortController !== "undefined" ? new AbortController() : null;

    fetch(
      url,
      FacetFiltersForm.abortController
        ? { signal: FacetFiltersForm.abortController.signal }
        : undefined
    )
      .then((response) => response.text())
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.latestRequestId) return; // stale
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          {
            html,
            url,
          },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        new FacetsToggle();
        new SidebarToggle();

        FacetFiltersForm.restoreUiState();
        FacetFiltersForm.unlockProductsHeightWhenReady();
      })
      .catch((error) => {
        if (error && error.name === "AbortError") return; // superseded
        throw error;
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    // A cached render is also "the latest state" — invalidate any fetch
    // that is still in flight so it can't land afterwards and overwrite us.
    ++FacetFiltersForm.latestRequestId;
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }

    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    new FacetsToggle();
    new SidebarToggle();

    FacetFiltersForm.restoreUiState();
    FacetFiltersForm.unlockProductsHeightWhenReady();
  }

  static renderProductGridContainer(html) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    // ROOT-CAUSE FIX for the closing accordions:
    // #ProductGridContainer wraps the WHOLE collection area — facets bar,
    // #Sidebar-Container AND the desktop filter sidebar. Replacing its
    // entire innerHTML bulldozed the sidebar on every filter apply,
    // recreating every dropdown in the server's default (closed) state and
    // erasing all the state preservation done in renderFilters.
    // Only the products wrapper is replaced now; the sidebar DOM is never
    // touched, so its open/closed state physically cannot be lost.
    const liveProducts = document.querySelector(
      "#ProductGridContainer .sidebar-container--products"
    );
    const newProducts = parsedHTML.querySelector(
      "#ProductGridContainer .sidebar-container--products"
    );

    if (liveProducts && newProducts) {
      liveProducts.innerHTML = newProducts.innerHTML;

      // Keep the results--N styling class on the sidebar container in sync
      // without touching its other state classes (e.g. facets--sidebar-open).
      // ONLY when the response actually contains the desktop filter form:
      // a zero-results response renders no filters and carries results--0,
      // which the theme styles as "no sidebar" — syncing that class while
      // we're deliberately KEEPING the live sidebar broke the layout.
      const responseHasSidebarFilters = !!parsedHTML.querySelector(
        ".facets-desktop-container form collapsible-row"
      );
      const liveSidebar = document.getElementById("Sidebar-Container");
      const newSidebar = parsedHTML.getElementById("Sidebar-Container");
      if (liveSidebar && newSidebar && responseHasSidebarFilters) {
        const newResults = Array.from(newSidebar.classList).find((c) =>
          c.startsWith("results--")
        );
        Array.from(liveSidebar.classList)
          .filter((c) => c.startsWith("results--"))
          .forEach((c) => liveSidebar.classList.remove(c));
        if (newResults) liveSidebar.classList.add(newResults);
      }
    } else {
      // Fallback for layouts without the products wrapper
      // (e.g. a zero-results search page that renders no sidebar).
      document.getElementById("ProductGridContainer").innerHTML =
        parsedHTML.getElementById("ProductGridContainer").innerHTML;
    }

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".lazyload")
      .forEach((image) => {
        lazySizes.loader.unveil(image);
      });

    document.dispatchEvent(new CustomEvent("facets:rendered"));
  }

  static renderProductCount(html) {
    const countHtml = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount");
    const container = document.getElementsByClassName("thb-filter-count");

    if (countHtml) {
      for (var item of container) {
        item.innerHTML = countHtml.innerHTML;
        item.classList.remove("loading");
      }
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    // Pair live forms with parsed forms BY POSITION, never by id — the
    // desktop sidebar and the filter drawer can share the same form id,
    // which previously made every parsed row (including the drawer's
    // closed copies) target the first form in the document.
    const liveForms = FacetFiltersForm.facetForms(document);
    const parsedForms = FacetFiltersForm.facetForms(parsedHTML);

    // The row the user is interacting with (skipped from re-rendering).
    const clickedRow =
      event && event.target && event.target.closest
        ? event.target.closest("collapsible-row")
        : null;

    let countsSource = null;

    FacetFiltersForm.pairForms(liveForms, parsedForms).forEach(({ parsedForm, liveForm }) => {
      if (!liveForm) return;

      const parsedRows = Array.from(
        parsedForm.querySelectorAll("collapsible-row")
      );
      const liveRows = Array.from(liveForm.querySelectorAll("collapsible-row"));
      if (!parsedRows.length && !liveRows.length) return;

      const wrapper = liveRows.length
        ? liveRows[0].parentNode
        : liveForm.querySelector(".facets__wrapper") || liveForm;

      // If this form's rows don't expose usable unique titles (the mobile
      // drawer's markup can differ from the desktop sidebar), title
      // reconciliation would collapse rows onto one key and make them
      // disappear. Fall back to a safe in-place update by data-index for
      // this form: contents refresh, state is preserved, and no row is
      // ever added or removed.
      if (
        !FacetFiltersForm.rowTitlesUsable(parsedRows) ||
        !FacetFiltersForm.rowTitlesUsable(liveRows)
      ) {
        parsedRows.forEach((parsedRow) => {
          const target = liveForm.querySelector(
            `collapsible-row[data-index="${parsedRow.dataset.index}"]`
          );
          if (!target) return;
          if (target === clickedRow) {
            countsSource = parsedRow;
            return;
          }
          FacetFiltersForm.updateRowPreservingState(target, parsedRow);
        });
        return;
      }

      // Match rows BY TITLE, not by data-index: Shopify omits filters with
      // no matching values from the response and renumbers the rest, so
      // positional matching writes the wrong filter's content into rows.
      const liveByTitle = new Map();
      liveRows.forEach((row) =>
        liveByTitle.set(FacetFiltersForm.rowTitle(row), row)
      );
      const parsedTitles = new Set(
        parsedRows.map((row) => FacetFiltersForm.rowTitle(row))
      );

      // Remove rows whose filter no longer exists in the response.
      liveRows.forEach((row) => {
        if (!parsedTitles.has(FacetFiltersForm.rowTitle(row))) row.remove();
      });

      // Walk the response's rows in order: update, insert, or reorder.
      let anchor = null;
      parsedRows.forEach((parsedRow) => {
        const title = FacetFiltersForm.rowTitle(parsedRow);
        let liveRow = liveByTitle.get(title) || null;
        const isClickedRow = !!liveRow && liveRow === clickedRow;

        if (!liveRow) {
          // A filter that wasn't on the page before — insert a fresh copy.
          liveRow = document.importNode(parsedRow, true);
          if (anchor) anchor.after(liveRow);
          else wrapper.insertBefore(liveRow, wrapper.firstChild);
        } else {
          if (isClickedRow) {
            // Never re-render the row being interacted with; only its
            // counts are refreshed below.
            countsSource = parsedRow;
          } else {
            FacetFiltersForm.updateRowPreservingState(liveRow, parsedRow);
          }

          // Reposition only when the order actually changed — moving a node
          // re-runs its custom element lifecycle, so avoid gratuitous moves.
          const expected = anchor
            ? anchor.nextElementSibling
            : wrapper.querySelector("collapsible-row");
          if (expected !== liveRow) {
            if (anchor) anchor.after(liveRow);
            else wrapper.insertBefore(liveRow, wrapper.firstChild);
          }
        }

        // Keep the positional index attribute in sync for anything that
        // still reads it.
        if (parsedRow.dataset.index !== undefined) {
          liveRow.dataset.index = parsedRow.dataset.index;
        }
        anchor = liveRow;
      });
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
    new FacetsToggle();
    new SidebarToggle();

    if (countsSource && clickedRow)
      FacetFiltersForm.renderCounts(countsSource, clickedRow);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [".mobile-filter-count"];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML =
        html.querySelector(selector).innerHTML;
      document.querySelector(selector).classList.remove("loading");
    });
  }

  static renderCounts(source, target) {
    const targetElement = target.querySelector(".facets__selected");
    const sourceElement = source.querySelector(".facets__selected");

    if (sourceElement && targetElement) {
      target.querySelector(".facets__selected").outerHTML =
        source.querySelector(".facets__selected").outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      {
        searchParams,
      },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    
    const formData = new FormData(event.target.closest("form"));
    const searchParams = new URLSearchParams();

    // Skip empty fields: unfilled price boxes would otherwise be sent as
    // "filter.v.price.gte=" and override the real range.
    for (const [key, value] of formData) {
      if (String(value).trim() === "") continue;
      searchParams.append(key, value);
    }

    // Price is a single range, so keep one value per bound. Other filters
    // (brand, type) can legitimately repeat and are left alone.
    ["filter.v.price.gte", "filter.v.price.lte"].forEach((key) => {
      const values = searchParams.getAll(key);
      if (values.length > 1) searchParams.set(key, values[values.length - 1]);
    });

    const ENFORCED_MIN_MAX = 1; // Minimum allowed max price

    // Delete min price if it's 0.00
    if (searchParams.get("filter.v.price.gte") === "0.00") {
      searchParams.delete("filter.v.price.gte");
    }

    // Get the slider max value from DOM
    const slider = document.querySelector(".price_slider");
    const sliderMax = slider ? parseFloat(slider.dataset.max) : 0;

    // Check max price value
    let maxPriceStr = searchParams.get("filter.v.price.lte");
    if (maxPriceStr) {
      let maxPriceVal = parseFloat(maxPriceStr);

      // Enforce minimum max price of 1
      if (maxPriceVal < ENFORCED_MIN_MAX) {
        maxPriceVal = ENFORCED_MIN_MAX;
        searchParams.set("filter.v.price.lte", maxPriceVal.toFixed(2));
      }

      // Remove max filter if equals slider max
      if (maxPriceVal === sliderMax) {
        searchParams.delete("filter.v.price.lte");
      }
    }

    FacetFiltersForm.renderPage(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }

  onMore(e) {
    let ul = this.closest(".thb-filter-content").querySelector("ul");

    if (ul.hasAttribute("filters-open")) {
      this.textContent = theme.strings.showMore;
      ul.removeAttribute("filters-open");
    } else {
      this.textContent = theme.strings.showLess;
      ul.setAttribute("filters-open", "");
    }
    e.preventDefault();
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.latestRequestId = 0;
FacetFiltersForm.abortController = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

/**
 *  @class
 *  @function FacetRemove
 */
class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("a").forEach((item) => {
      item.addEventListener("click", (event) => {
        event.preventDefault();
        const form =
          this.closest("facet-filters-form") ||
          document.querySelector("facet-filters-form");
        form.onActiveFilterClick(event);
      });
    });
  }
}

customElements.define("facet-remove", FacetRemove);

/**
 *  @class
 *  @function FacetToolbar
 */
class FacetToolbar extends HTMLElement {
  constructor() {
    super();
    this._productLayout = null;
    this.mutationObserver = null;
  }

  connectedCallback() {
    this.productGrid = document.querySelector("#ProductGridContainer");
    this.compareToggle = this.querySelector("#compare_toggle");
    this.layoutSwitcher = this.querySelector(".facets--bar-view-as");
    this.buttons = this.querySelectorAll(".facets--bar-view-as-button");

    this.productLayout =
      window.localStorage.getItem("product-layout") ||
      this.productGrid.dataset.layout;

    if (this.layoutSwitcher) {
      this.buttons.forEach((button) => {
        button.addEventListener("click", (evt) => {
          this.buttons.forEach((el) => el.removeAttribute("disabled"));
          this.productLayout = button.dataset.value;
          evt.preventDefault();
        });
      });

      const toggle = this.querySelector(
        `.facets--bar-view-as-button-${this.productLayout}`
      );
      if (toggle) toggle.click();
      else
        this.updateProductCardsLayout(
          Array.from(this.productGrid.querySelectorAll("product-card"))
        );
    }

    if (this.compareToggle) {
      this.compareToggle.addEventListener("change", this.onCompare);
    }

    // Safe MutationObserver: only process added product cards
    if (this.productGrid) {
      this.mutationObserver = new MutationObserver((mutations) => {
        const newCards = [];
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.tagName === "PRODUCT-CARD") newCards.push(node);
            else if (node.querySelectorAll) {
              newCards.push(...node.querySelectorAll("product-card"));
            }
          });
        });
        if (newCards.length) this.updateProductCardsLayout(newCards);
      });

      this.mutationObserver.observe(this.productGrid, {
        childList: true,
        subtree: true,
      });
    }

    // Listen for pagination event (just for logging or other actions)
    document.addEventListener("pagination:page-changed", (event) => {
      const { url } = event.detail;
      // The observer handles layout updates automatically
    });
  }

  disconnectedCallback() {
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }

  get productLayout() {
    return this._productLayout;
  }

  set productLayout(value) {
    if (this._productLayout === value) return;
    this._productLayout = value;

    if (this.productGrid) this.productGrid.dataset.layout = value;
    window.localStorage.setItem("product-layout", value);

    // Update button states to show which one is selected
    if (this.buttons) {
      this.buttons.forEach((el) => el.removeAttribute("disabled"));
      const activeButton = this.querySelector(
        `.facets--bar-view-as-button-${value}`
      );
      if (activeButton) activeButton.setAttribute("disabled", "");
    }

    // Apply layout only to current product cards
    const cards = Array.from(this.productGrid.querySelectorAll("product-card"));
    this.updateProductCardsLayout(cards);
  }

  updateProductCardsLayout(cards) {
    cards.forEach((card) => {
      const image = card.querySelector(".product-card-image");
      const infoContainer = card.querySelector(".product-card-info");
      const info = card.querySelector(".product-card-info--inner");
      const rating = card.querySelector(".star-rating-container");
      const buttons = card.querySelector(".product-card-info--bottom");

      if (!image || !info || !rating || !buttons) return;

      let infoWrapper = card.querySelector(".product-info-rating-wrapper");
      let flexWrapper = card.querySelector(".product-list-flex-wrapper");

      if (this._productLayout === "list") {
        if (!flexWrapper) {
          flexWrapper = document.createElement("div");
          flexWrapper.className = "product-list-flex-wrapper";
          flexWrapper.style.width = "100%";
          flexWrapper.style.display = "flex";
          flexWrapper.style.flexDirection = "row";
          flexWrapper.style.alignItems = "center";
          flexWrapper.style.justifyContent = "space-between";
          flexWrapper.style.paddingRight = "25px";
        }

        if (!infoWrapper) {
          infoWrapper = document.createElement("div");
          infoWrapper.className = "product-info-rating-wrapper";
        }

        infoWrapper.appendChild(info);
        infoWrapper.appendChild(rating);

        flexWrapper.innerHTML = "";
        flexWrapper.appendChild(infoWrapper);
        flexWrapper.appendChild(buttons);

        if (image.nextSibling) {
          card.insertBefore(flexWrapper, image.nextSibling);
        } else {
          card.appendChild(flexWrapper);
        }

        if (infoContainer && infoContainer.children.length === 0) {
          infoContainer.remove();
        }
      } else {
        let restoredInfoContainer = card.querySelector(".product-card-info");
        if (!restoredInfoContainer) {
          restoredInfoContainer = document.createElement("div");
          restoredInfoContainer.className = "product-card-info";
          card.appendChild(restoredInfoContainer);
        }

        restoredInfoContainer.appendChild(info);
        restoredInfoContainer.appendChild(rating);
        restoredInfoContainer.appendChild(buttons);

        if (flexWrapper) flexWrapper.remove();
        if (infoWrapper && infoWrapper.parentNode !== restoredInfoContainer)
          infoWrapper.remove();
      }
    });
  }

  onCompare(e) {
    document.body.classList.toggle("compare-true");
    e.preventDefault();
  }
}

customElements.define("facet-toolbar", FacetToolbar);

/**
 *  @class
 *  @function PriceSlider
 */
class PriceSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.slider = this.querySelector(".price_slider");
    this.amounts = this.querySelector(".price_slider_amount");
    this.form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    this.inputEvent = new CustomEvent("input");

    const args = {
      start: [
        parseFloat(this.slider.dataset.minValue || 0),
        parseFloat(this.slider.dataset.maxValue || this.slider.dataset.max),
      ],
      connect: true,
      step: 0.01,
      direction: document.dir,
      handleAttributes: [{ "aria-label": "lower" }, { "aria-label": "upper" }],
      range: {
        min: 0,
        max: parseFloat(this.slider.dataset.max),
      },
    };
    noUiSlider.create(this.slider, args);

    this.slider.noUiSlider.on("update", this.onSliderUpdate.bind(this));
    this.slider.noUiSlider.on("change", this.onSliderChange.bind(this));
  }
  onSliderUpdate(values) {
    const minValue = parseFloat(values[0]);
    const maxValue = parseFloat(values[1]);

    // Format: remove .00 but keep other decimals like .14, .50, etc.
    const formatPrice = (value) => {
      const fixed = value.toFixed(2);
      // If it ends with .00, remove it, otherwise keep the decimals
      return fixed.endsWith(".00") ? fixed.slice(0, -3) : fixed;
    };

    this.amounts.querySelector(".field__input_min").value = formatPrice(minValue);
    this.amounts.querySelector(".field__input_max").value = formatPrice(maxValue);

    // Update the display text if it exists
    const minDisplay = document.querySelector(".min-price-display");
    const maxDisplay = document.querySelector(".max-price-display");

    if (minDisplay) {
      minDisplay.textContent = formatPrice(minValue);
    }
    if (maxDisplay) {
      maxDisplay.textContent = formatPrice(maxValue);
    }
  }

  onSliderChange(values) {
    this.form.querySelector("form").dispatchEvent(this.inputEvent);
  }
}
customElements.define("price-slider", PriceSlider);

/**
 *  @class
 *  @function PercentSavedSort
 */
class PercentSavedSort {
  constructor() {
    this.init();
    this.setupEventListeners();
  }

  init() {
    const urlParams = new URLSearchParams(window.location.search);
    const currentSort = urlParams.get("sort_by");

    if (currentSort === "percent-saved") {
      this.sortProductsByDiscount();
    }
  }

  setupEventListeners() {
    const sortSelect = document.getElementById("SortByBar");

    if (sortSelect) {
      sortSelect.addEventListener("change", (e) => {
        if (e.target.value === "percent-saved") {
          e.preventDefault();
          this.sortProductsByDiscount();
          this.updateURL();
        }
      });
    }

    document.addEventListener("facets:rendered", () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("sort_by") === "percent-saved") {
        this.sortProductsByDiscount();
      }
    });
  }

  sortProductsByDiscount() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    const productItems = Array.from(
      productGrid.querySelectorAll(
        "li.columns:not(.promotion-column):not(.loading-overlay)"
      )
    );

    if (productItems.length === 0) return;

    const productsWithData = productItems
      .map((item) => {
        const card = item.querySelector("product-card");
        if (!card) return null;

        let discountPercent = this.calculateDiscount(card);

        return {
          element: item,
          discount: discountPercent,
          hasDiscount: discountPercent > 0,
        };
      })
      .filter((item) => item !== null);

    // Sort: products with discounts first (highest %), then no discount
    productsWithData.sort((a, b) => {
      if (a.hasDiscount && !b.hasDiscount) return -1;
      if (!a.hasDiscount && b.hasDiscount) return 1;
      return b.discount - a.discount;
    });

    const loadingOverlay = productGrid.querySelector("li.loading-overlay");

    productsWithData.forEach((item) => {
      productGrid.appendChild(item.element);
    });

    if (loadingOverlay) {
      productGrid.appendChild(loadingOverlay);
    }
  }

  calculateDiscount(card) {
    // Your price structure uses <ins> for current price and <del> for compare price
    const currentPriceEl = card.querySelector(".price ins .amount");
    const comparePriceEl = card.querySelector(".price del .amount");

    let comparePrice = 0;
    let currentPrice = 0;

    // Get compare at price (original price in <del> tag)
    if (comparePriceEl) {
      const priceText = comparePriceEl.textContent.replace(/[^0-9.]/g, "");
      comparePrice = parseFloat(priceText);
    }

    // Get current price (sale price in <ins> tag)
    if (currentPriceEl) {
      const priceText = currentPriceEl.textContent.replace(/[^0-9.]/g, "");
      currentPrice = parseFloat(priceText);
    }

    // Calculate discount percentage
    if (comparePrice > 0 && currentPrice > 0 && comparePrice > currentPrice) {
      const discount = ((comparePrice - currentPrice) / comparePrice) * 100;
      return discount;
    }

    return 0;
  }

  updateURL() {
    const url = new URL(window.location);
    url.searchParams.set("sort_by", "percent-saved");
    window.history.pushState({}, "", url);
  }
}

window.addEventListener("load", () => {
  new FacetsToggle();
  new SidebarToggle();
  new PercentSavedSort();
});